import { TZDate } from "react-day-picker";
import {
  addMinutes,
  type Duration,
  formatDuration,
  intervalToDuration,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { DateTime } from "luxon";

import { dateFormat } from "@/constants/dateFormat";

import { DATE_FNS_LOCALES, SupportedLocale } from "../../../scripts/i18n/types";

export type CalendarDayBounds = {
  startTime: string;
  endTime: string;
};

export class DateUtils {
  /**
   * Converts seconds into a human-readable duration string with locale support.
   * Uses date-fns for localization and duration formatting.
   *
   * @param seconds - Duration in seconds to format
   * @param maxUnits - Maximum number of time units to include in output (optional).
   *                  For example, maxUnits=2 shows hours and minutes but omits seconds
   * @param locale - Locale code from SupportedLocale enum (e.g. 'en', 'ja').
   *                Uses system locale if not provided
   * @param abbr - Specifies how much to abbreviate the units
   *               (e.g. 'hrs' vs 'h'). Uses "FULL" if not provided
   * @param minUnit - Minimum unit to display. If "minutes", seconds will be excluded.
   *                  When all format units are zero (e.g., 1-59 seconds with minUnit: "minutes"),
   *                  displays "0" of the minUnit instead of empty string.
   * @returns Localized duration string with appropriate spacing and formatting
   *
   * @example
   * // English examples
   * secondsToDuration(3665)              // "1 hour 1 minute 5 seconds"
   * secondsToDuration(3665, 2)           // "1 hour 1 minute"
   * secondsToDuration(7200)              // "2 hours"
   * secondsToDuration(7200, 2, 'en', "SHORTENED")  // "2hr 1min"
   * secondsToDuration(7200, 2, 'en', "LETTER")  // "2h 1m"
   * secondsToDuration(30, undefined, undefined, undefined, "minutes")  // "0mins"
   *
   * // Japanese examples with locale
   * secondsToDuration(3665, 2, 'ja')     // "1時間 1分"
   * secondsToDuration(7200, 1, 'ja')     // "2時間"
   */
  static secondsToDuration(
    seconds: number,
    maxUnits?: number,
    locale?: string,
    abbr?: DurationAbbreviation,
    minUnit?: "seconds" | "minutes",
  ) {
    const duration = intervalToDuration({
      start: 0,
      end: Math.abs(seconds) * 1000,
    });

    const allUnits = [
      "years",
      "months",
      "days",
      "hours",
      "minutes",
      "seconds",
    ] as const;
    const minUnitIndex = minUnit
      ? allUnits.indexOf(minUnit)
      : allUnits.length - 1;
    const formatArray = allUnits.slice(0, minUnitIndex + 1);

    const options = {
      format: formatArray,
      locale: locale ? DATE_FNS_LOCALES[locale as SupportedLocale] : enUS,
      zero: false,
    };

    // Check if all units in the format array are zero
    // This handles cases like 1-59 seconds with minUnit: "minutes" where
    // seconds are excluded but minutes is 0, resulting in empty output
    const allFormatUnitsAreZero = formatArray.every(
      (unit) => (duration[unit] ?? 0) === 0,
    );

    // handles when the duration is zero or all format units are zero
    if (seconds === 0 || allFormatUnitsAreZero) {
      options.format = [minUnit || "seconds"];
      options.zero = true;
    }

    // date-fns v3+ omits zero units from intervalToDuration; formatDuration skips
    // undefined values even when zero: true, so fill requested format units.
    const formatUnits = options.format ?? formatArray;
    const normalizedDuration: Duration = { ...duration };
    for (const unit of formatUnits) {
      if (normalizedDuration[unit] === undefined) {
        normalizedDuration[unit] = 0;
      }
    }

    const formatted = formatDuration(normalizedDuration, options);

    // Split on any whitespace or between digits and non-digits
    const parts = formatted.split(/(?<=\d)(?=\D)|[\s]+/);
    const pairs = [];

    for (let i = 0; i < parts.length; i += 2) {
      if (parts[i + 1]) {
        pairs.push(parts[i] + parts[i + 1]);
      }
    }

    const separator =
      pairs.filter((pair) => pair.includes(" ")).length > 0 ? " " : "";

    const fullLengthResult = maxUnits
      ? pairs.slice(0, maxUnits).join(separator)
      : pairs.join(separator);

    if (!abbr || abbr == "FULL") {
      return fullLengthResult;
    }
    return this.shortenDuration(fullLengthResult, abbr);
  }

  /**
   * Converts a duration in seconds to parameters suitable for FormatJS's
   * FormattedRelativeTime. Finds the largest non-zero time unit and returns
   * its value with appropriate sign.
   *
   * @param seconds - Number of seconds (positive for future, negative for
   * past)
   * @returns Object containing:
   *  - value: Number of units (signed)
   *  - unit: Most significant time unit present
   * @example
   * getRelativeTimeParams(-7200) -> { value: -2, unit: "hour" }
   * getRelativeTimeParams(-2592000)   -> { value: -1, unit: "month" }
   */
  static getRelativeTimeParams(seconds: number): {
    value: number;
    unit: "year" | "month" | "day" | "hour" | "minute" | "second";
  } {
    const duration = intervalToDuration({
      start: 0,
      end: Math.abs(seconds) * 1000,
    });

    const units = [
      { key: "years", unit: "year" },
      { key: "months", unit: "month" },
      { key: "days", unit: "day" },
      { key: "hours", unit: "hour" },
      { key: "minutes", unit: "minute" },
      { key: "seconds", unit: "second" },
    ] as const;

    const match =
      units.find(({ key }) => duration[key]) ?? units[units.length - 1]; // Always matches at least seconds
    return {
      value: Math.sign(seconds) * (duration[match.key] ?? 0),
      unit: match.unit,
    };
  }

  /**
   * Shortens the units in a formatted duration.
   * @param {string} duration           The formatted duration to shorten.
   * @param {DurationAbbreviation} abbr Specifies how much to abbreviate the
   *                                    units (e.g. 'hrs' vs 'h').
   * @returns {string}                  The duration with the units shortened as specified.
   */
  static shortenDuration(duration: string, abbr: DurationAbbreviation) {
    switch (abbr) {
      case "FULL":
        return duration;
      case "LETTER":
        return duration
          .replace(" years", "y")
          .replace(" year", "y")
          .replace(" months", "mo")
          .replace(" month", "mo")
          .replace(" days", "d")
          .replace(" day", "d")
          .replace(" hours", "h")
          .replace(" hour", "h")
          .replace(" minutes", "m")
          .replace(" minute", "m")
          .replace(" seconds", "s")
          .replace(" second", "s");
      case "SHORTENED":
        return duration
          .replace(" years", " yrs")
          .replace(" year", " yr")
          .replace(" months", " mo")
          .replace(" month", " mo")
          .replace(" days", " days")
          .replace(" day", " day")
          .replace(" hours", " hrs")
          .replace(" hour", " hr")
          .replace(" minutes", " mins")
          .replace(" minute", " min")
          .replace(" seconds", " sec")
          .replace(" second", " sec");
    }
  }

  /**
   * Converts seconds into a human-readable duration string with the units
   * abbreviated with locale support. Uses date-fns for localization and
   * duration formatting.
   * @param seconds - Duration in seconds to format.
   * @param maxUnits - Maximum number of time units to include in output (optional).
   *                  For example, maxUnits=2 shows hours and minutes but omits seconds.
   * @param locale - Locale code from SupportedLocale enum (e.g. 'en', 'ja').
   *                Uses system locale if not provided.
   * @returns Localized duration string with appropriate spacing and formatting.
   */
  static secondsToShortDuration(
    seconds: number,
    maxUnits?: number,
    locale?: string,
  ) {
    return DateUtils.secondsToDuration(seconds, maxUnits, locale, "LETTER");
  }

  /**
   * Returns the UTC ISO start/end bounds for a calendar day in the given
   * timezone. Useful for building time-range filters from a single date.
   *
   * `startTime` is local midnight, `endTime` is the next local midnight
   * (exclusive). Both are returned as UTC ISO-8601 strings.
   *
   * @param date - A `yyyy-MM-dd` string or a `Date` object
   * @param timeZone - IANA timezone identifier (e.g. `"America/New_York"`, `"Asia/Kolkata"`)
   * @returns `{ startTime, endTime }` in UTC ISO format, or `null` if the input is invalid
   *
   * @example
   * // IST (UTC+5:30): local midnight → UTC
   * getCalendarDayBounds("2025-06-04", "Asia/Kolkata")
   * // { startTime: "2025-06-03T18:30:00.000Z", endTime: "2025-06-04T18:30:00.000Z" }
   *
   * @example
   * // With a Date object
   * getCalendarDayBounds(new Date("2025-06-04"), "UTC")
   * // { startTime: "2025-06-04T00:00:00.000Z", endTime: "2025-06-05T00:00:00.000Z" }
   */
  static getCalendarDayBounds(
    date: string | Date,
    timeZone: string,
  ): CalendarDayBounds | null {
    let year: number, month: number, day: number;

    if (date instanceof Date) {
      if (Number.isNaN(date.getTime())) return null;
      year = date.getFullYear();
      month = date.getMonth();
      day = date.getDate();
    } else {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
      year = Number(date.slice(0, 4));
      month = Number(date.slice(5, 7)) - 1;
      day = Number(date.slice(8, 10));
    }

    const start = new TZDate(year, month, day, timeZone);
    const end = new TZDate(year, month, day + 1, timeZone);

    return {
      startTime: new Date(start.getTime()).toISOString(),
      endTime: new Date(end.getTime()).toISOString(),
    };
  }

  static toUTCString(date?: Date) {
    if (!date) {
      return;
    }
    return date.toUTCString();
  }
  private static getTimezoneOffsetInMinutes(tzCode: string) {
    return DateTime.local().setZone(tzCode).offset;
  }

  static zonedToUtcStartTime(dateString?: string, tzCode?: string) {
    if (!dateString) return;
    if (!tzCode) return dateString;
    const tzOffsetMins = this.getTimezoneOffsetInMinutes(tzCode);
    const startTimeInUtc = toZonedTime(
      new Date(dateString),
      "UTC",
    ).toISOString();

    return addMinutes(new Date(startTimeInUtc), tzOffsetMins).toISOString();
  }

  static zonedToUtcEndTime(dateString?: string, tzCode?: string) {
    if (!dateString) return;
    if (!tzCode) return dateString;
    const tzOffsetMins = this.getTimezoneOffsetInMinutes(tzCode);
    const endTimeInUtc = toZonedTime(new Date(dateString), "UTC").toISOString();
    const endTimeWithOffset = addMinutes(
      new Date(endTimeInUtc),
      tzOffsetMins,
    ).toISOString();
    const now = new Date().toISOString();
    return endTimeWithOffset > now ? now : endTimeWithOffset;
  }

  static getLastCheckInTimeContent(date: string, tzKey: string): string {
    const day = formatInTimeZone(new Date(date), tzKey, dateFormat.monthDDayYY);
    const time = formatInTimeZone(
      new Date(date),
      tzKey,
      dateFormat.hoursMinutes12,
    );

    return `${day} @ ${time}`;
  }

  /**
   * Converts a date string to a relative time object.
   * @param dateString - The date string to convert.
   * @returns The relative time object.
   */
  static getRelativeTime(dateString: string | null | undefined) {
    if (!dateString) return null;

    const now = new Date();
    const checkInTime = new Date(dateString);
    const diffInSeconds = Math.floor(
      (now.getTime() - checkInTime.getTime()) / 1000,
    );

    // Always use absolute value to avoid "in" prefix
    const absDiffInSeconds = Math.abs(diffInSeconds);

    // Convert to appropriate unit
    if (absDiffInSeconds < 60) {
      return { value: absDiffInSeconds, unit: "second" as const };
    } else if (absDiffInSeconds < 3600) {
      return {
        value: Math.floor(absDiffInSeconds / 60),
        unit: "minute" as const,
      };
    } else if (absDiffInSeconds < 86400) {
      return {
        value: Math.floor(absDiffInSeconds / 3600),
        unit: "hour" as const,
      };
    } else {
      return {
        value: Math.floor(absDiffInSeconds / 86400),
        unit: "day" as const,
      };
    }
  }
}

type DurationAbbreviation = "FULL" | "SHORTENED" | "LETTER";
